import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/AdminLayout';
import DocumentManager from '../../components/DocumentManager';
import { 
  ArrowLeft, Calendar, User, DollarSign, Clock, 
  CheckCircle, XCircle, AlertCircle, Plus, Edit2, 
  Trash2, FileText, Upload, Eye, Download,
  PlayCircle, PauseCircle, RotateCcw, Flag
} from 'lucide-react';
import { format, formatDistance } from 'date-fns';

const ProjectDetail = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddTask, setShowAddTask] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`/api/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      toast.error('Failed to fetch project details');
    } finally {
      setLoading(false);
    }
  };

  const updateProjectStatus = async (status) => {
    try {
      await axios.put(`/api/projects/${id}/status`, { status });
      setProject({ ...project, status });
      toast.success('Project status updated');
    } catch (error) {
      toast.error('Failed to update project status');
    }
  };

  const addTask = async (data) => {
    try {
      await axios.post(`/api/projects/${id}/tasks`, data);
      toast.success('Task added successfully');
      setShowAddTask(false);
      reset();
      fetchProject();
    } catch (error) {
      toast.error('Failed to add task');
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await axios.put(`/api/projects/${id}/tasks/${taskId}`, { status });
      toast.success('Task status updated');
      fetchProject();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const updatePaymentStatus = async (paymentId, status, notes = '') => {
    try {
      await axios.put(`/api/projects/${id}/payments/${paymentId}`, { status, notes });
      toast.success('Payment status updated');
      fetchProject();
    } catch (error) {
      toast.error('Failed to update payment status');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'in-progress': 'bg-blue-100 text-blue-800 border-blue-200',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'on-hold': 'bg-gray-100 text-gray-800 border-gray-200',
      'review': 'bg-purple-100 text-purple-800 border-purple-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'urgent': 'text-red-500',
      'high': 'text-orange-500',
      'medium': 'text-yellow-500',
      'low': 'text-green-500'
    };
    return colors[priority] || 'text-gray-500';
  };

  const getTaskIcon = (status) => {
    const icons = {
      'pending': <Clock className="w-4 h-4" />,
      'in-progress': <PlayCircle className="w-4 h-4" />,
      'completed': <CheckCircle className="w-4 h-4" />
    };
    return icons[status] || <Clock className="w-4 h-4" />;
  };

  const calculateProgress = () => {
    if (!project?.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(task => task.status === 'completed').length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  const getTotalPaid = () => {
    if (!project?.payments) return 0;
    return project.payments
      .filter(payment => payment.status === 'verified')
      .reduce((sum, payment) => sum + payment.amount, 0);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!project) {
    return (
      <AdminLayout>
        <div className="text-center py-8">
          <p className="text-gray-500">Project not found</p>
          <Link to="/admin/projects" className="text-primary-600 hover:text-primary-700 mt-4 inline-block">
            ← Back to Projects
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link 
              to="/admin/projects" 
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Projects
            </Link>
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={project.status}
              onChange={(e) => updateProjectStatus(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm font-medium ${getStatusColor(project.status)}`}
            >
              <option value="pending">Pending</option>
              <option value="in-progress">In Progress</option>
              <option value="review">Review</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Project Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Project Info */}
            <div className="lg:col-span-2">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-800 mb-2">{project.title}</h1>
                  <p className="text-gray-600 mb-2">{project.description}</p>
                  <p className="text-sm text-gray-500">Project ID: {project.projectId}</p>
                </div>
                <div className={`flex items-center px-3 py-1 rounded-full ${getPriorityColor(project.priority)}`}>
                  <Flag className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium capitalize">{project.priority}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Project Progress</span>
                  <span className="text-sm text-gray-500">{calculateProgress()}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${calculateProgress()}%` }}
                  ></div>
                </div>
              </div>

              {/* Project Details */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex items-center text-gray-600">
                  <User className="w-4 h-4 mr-2" />
                  {project.student?.name}
                </div>
                <div className="flex items-center text-gray-600">
                  <Calendar className="w-4 h-4 mr-2" />
                  {format(new Date(project.startDate), 'MMM dd')} - {format(new Date(project.endDate), 'MMM dd, yyyy')}
                </div>
                <div className="flex items-center text-gray-600">
                  <DollarSign className="w-4 h-4 mr-2" />
                  LKR {project.totalAmount?.toLocaleString()}
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" />
                  {formatDistance(new Date(project.endDate), new Date(), { addSuffix: true })}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">{project.tasks?.length || 0}</div>
                <div className="text-blue-100 text-sm">Total Tasks</div>
              </div>
              <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">LKR {getTotalPaid().toLocaleString()}</div>
                <div className="text-green-100 text-sm">Amount Paid</div>
              </div>
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                <div className="text-2xl font-bold">{calculateProgress()}%</div>
                <div className="text-purple-100 text-sm">Completed</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Requirements */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            Project Requirements
          </h3>
          <p className="text-gray-700 whitespace-pre-wrap">{project.requirements}</p>
        </motion.div>

        {/* Tasks Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold">Task Management</h3>
            <button
              onClick={() => setShowAddTask(true)}
              className="btn-primary flex items-center"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Task
            </button>
          </div>

          <div className="space-y-4">
            {project.tasks?.map((task, index) => (
              <motion.div
                key={task._id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`flex items-center ${task.status === 'completed' ? 'text-green-600' : task.status === 'in-progress' ? 'text-blue-600' : 'text-gray-600'}`}>
                        {getTaskIcon(task.status)}
                      </div>
                      <h4 className="font-semibold text-gray-800">{task.title}</h4>
                      {task.isAdditional && (
                        <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full">
                          Additional
                        </span>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-gray-600 mb-2">{task.description}</p>
                    )}
                    {task.dueDate && (
                      <p className="text-sm text-gray-500">
                        Due: {format(new Date(task.dueDate), 'MMM dd, yyyy')}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <select
                      value={task.status}
                      onChange={(e) => updateTaskStatus(task._id, e.target.value)}
                      className="text-sm border border-gray-300 rounded px-2 py-1"
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
              </motion.div>
            ))}

            {(!project.tasks || project.tasks.length === 0) && (
              <div className="text-center py-8 text-gray-500">
                No tasks yet. Add your first task to get started!
              </div>
            )}
          </div>
        </motion.div>

        {/* Payment Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-lg font-semibold mb-6 flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Payment Management
          </h3>

          <div className="space-y-4">
            {project.payments?.map((payment, index) => (
              <div key={payment._id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="font-semibold text-gray-800 capitalize">
                        {payment.type} Payment
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </div>
                    <p className="text-lg font-bold text-gray-800">
                      LKR {payment.amount?.toLocaleString()}
                    </p>
                    {payment.paymentDate && (
                      <p className="text-sm text-gray-500">
                        Paid: {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                      </p>
                    )}
                    {payment.paymentSlip && (
                      <a 
                        href={payment.paymentSlip} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary-600 hover:text-primary-700 text-sm flex items-center mt-2"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Payment Slip
                      </a>
                    )}
                  </div>
                  
                  {payment.status === 'pending' && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => updatePaymentStatus(payment._id, 'paid')}
                        className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                      >
                        Mark as Paid
                      </button>
                    </div>
                  )}
                  {payment.status === 'paid' && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => updatePaymentStatus(payment._id, 'verified')}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                      >
                        Verify
                      </button>
                      <button
                        onClick={() => updatePaymentStatus(payment._id, 'pending', 'Payment rejected')}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Document Management */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <DocumentManager 
            project={project} 
            onDocumentUpdate={fetchProject}
            userRole="admin"
          />
        </motion.div>
      </div>

      {/* Add Task Modal */}
      {showAddTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold mb-4">Add New Task</h3>
            <form onSubmit={handleSubmit(addTask)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Title</label>
                <input
                  {...register('title', { required: 'Title is required' })}
                  className="input-field"
                  placeholder="Enter task title"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  {...register('description')}
                  className="input-field"
                  rows="3"
                  placeholder="Task description (optional)"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  {...register('dueDate')}
                  className="input-field"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register('isAdditional')}
                  className="mr-2"
                />
                <label className="text-sm text-gray-700">Mark as additional task</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Payment (LKR) - Optional</label>
                <input
                  type="number"
                  {...register('additionalPayment')}
                  className="input-field"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">Add extra payment for this task if required</p>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" className="flex-1 btn-primary">
                  Add Task
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddTask(false);
                    reset();
                  }}
                  className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ProjectDetail;